import type { TenantCancellationRule } from '../../api/account/accountApiTypes';
import { categorySettingsOf, describeUseCase } from '../../utils/useCases';

/**
 * The cancellation section of a category's form (M10, #3042; PO decisions
 * 2026-10-19), as form state and back.
 *
 * The SERVER copy enforces (`new-backend/admin/src/lib/booking/cancellationRule.ts`,
 * pinned with the other copies by
 * `shared/cancellation/cancellation-rule-vectors.json`, which
 * `cancellationRules.test.ts` reads too). This file mirrors its
 * bounds only so the form can say what is wrong before saving — a 400 the
 * operator cannot connect to a field is the failure it avoids. If the two ever
 * disagree, the server's answer is shown (`errorMessageFrom`).
 */

/** The four answers a category can give. `inherit` exists on further categories only. */
export const CANCELLATION_MODES = ['inherit', 'none', 'free', 'tiers'] as const;
export type CancellationMode = (typeof CANCELLATION_MODES)[number];

export interface CancellationTierDraft {
    /** A row identity for rendering only — never sent. */
    id: string;
    daysBefore: string;
    percent: string;
}

let nextTierId = 0;
/** A fresh row identity. */
export const newTierId = (): string => {
    nextTierId += 1;
    return `tier-${nextTierId}`;
};

export interface CancellationDraft {
    mode: CancellationMode;
    tiers: CancellationTierDraft[];
    agbConfirmed: boolean;
    /** Whether the category had a value of its own when the form opened — an
     *  untouched "inherits" then sends nothing rather than a clearing null. */
    stored: boolean;
    /** Whether the operator changed anything in the section. An untouched
     *  STANDARD category sends no `cancellation`, so the server keeps what it
     *  has (review of #3394). */
    touched?: boolean;
}

/**
 * Every edit of the section goes through here: it marks the section touched
 * and takes the AGB tick back, so a changed rule — another tier, preset or
 * mode — can never be saved on the tick given for the previous one (PO
 * decision 3; review of #3394).
 */
export const editedDraft = (current: CancellationDraft, change: Partial<CancellationDraft>): CancellationDraft => ({
    ...current,
    ...change,
    agbConfirmed: false,
    touched: true,
});

/** Mirrors of the server's bounds. */
export const MAX_CANCELLATION_TIERS = 5;
export const MAX_DAYS_BEFORE = 365;

/**
 * Presets (PO decision 2): a pre-filled starting point the tenant edits. A
 * preset never applies by itself — the code default stays "no rule".
 */
export const CANCELLATION_PRESETS = {
    halls: [{ daysBefore: 14, percent: 50 }, { daysBefore: 3, percent: 90 }],
    eventVenues: [
        { daysBefore: 30, percent: 50 },
        { daysBefore: 14, percent: 80 },
        { daysBefore: 2, percent: 90 },
    ],
    shortNotice: [{ daysBefore: 2, percent: 50 }, { daysBefore: 1, percent: 90 }],
} as const;
export type CancellationPreset = keyof typeof CANCELLATION_PRESETS;
export const CANCELLATION_PRESET_KEYS = Object.keys(CANCELLATION_PRESETS) as CancellationPreset[];

/**
 * Where the rule is configurable (PO decision 5). Mirrors
 * `cancellationOnUseCase` / `cancellationOnCategory` in the server copy:
 * the use case (= its standard category) for rooms and station units; a
 * further category for rooms only. Handover use cases have none.
 */
export const cancellationOnUseCase = (useCase: string): boolean => {
    const template = describeUseCase(useCase)?.template;
    return template === 'space' || template === 'stationUnit';
};
export const cancellationOnCategory = (useCase: string): boolean =>
    categorySettingsOf(useCase)?.categoryCancellation === true;

const tierDrafts = (tiers: ReadonlyArray<{ daysBefore: number, percent: number }>): CancellationTierDraft[] =>
    [...tiers]
        .sort((a, b) => b.daysBefore - a.daysBefore)
        .map((tier) => ({ id: newTierId(), daysBefore: String(tier.daysBefore), percent: String(tier.percent) }));

/**
 * Form state for a stored value. On the standard category there is nothing to
 * inherit from: absent = "no rule".
 */
export const cancellationDraftFrom = (
    value: TenantCancellationRule | 'none' | null | undefined,
    isStandard: boolean,
): CancellationDraft => {
    const stored = value !== undefined && value !== null;
    if (!stored) return { mode: isStandard ? 'none' : 'inherit', tiers: [], agbConfirmed: false, stored };
    if (value === 'none') return { mode: 'none', tiers: [], agbConfirmed: false, stored };
    return {
        mode: value.tiers.length === 0 ? 'free' : 'tiers',
        tiers: tierDrafts(value.tiers),
        agbConfirmed: value.agbConfirmed === true,
        stored,
    };
};

/** The draft change for choosing a preset: its tiers, the tick left to the tenant. */
export const presetDraft = (preset: CancellationPreset, current: CancellationDraft): CancellationDraft =>
    editedDraft(current, { mode: 'tiers', tiers: tierDrafts(CANCELLATION_PRESETS[preset]) });

/** The preset the tiers match, or `custom`. */
export const presetOf = (draft: CancellationDraft): CancellationPreset | 'custom' =>
    CANCELLATION_PRESET_KEYS.find((preset) => {
        const tiers = tierDrafts(CANCELLATION_PRESETS[preset]);
        const own = tierDrafts(parsedTiers(draft) ?? []);
        return own.length === tiers.length
            && own.every((tier, i) => tier.daysBefore === tiers[i]?.daysBefore && tier.percent === tiers[i]?.percent);
    }) ?? 'custom';

const wholeIn = (raw: string, min: number, max: number): number | null => {
    const text = raw.trim();
    if (!/^\d+$/.test(text)) return null;
    const value = Number(text);
    return value >= min && value <= max ? value : null;
};

/** The tiers as numbers, or null when one row is not a valid number. */
const parsedTiers = (draft: CancellationDraft): Array<{ daysBefore: number, percent: number }> | null => {
    const tiers = draft.tiers.map((tier) => ({
        daysBefore: wholeIn(tier.daysBefore, 0, MAX_DAYS_BEFORE),
        percent: wholeIn(tier.percent, 1, 100),
    }));
    if (tiers.some((tier) => tier.daysBefore === null || tier.percent === null)) return null;
    return tiers as Array<{ daysBefore: number, percent: number }>;
};

/**
 * Why the draft cannot be saved, as a `services.category_config.cancellation_*`
 * key suffix — or null. Only `tiers` and `free` carry a rule, and both need
 * the tick (PO decision 3).
 */
export const cancellationDraftInvalid = (draft: CancellationDraft): string | null => {
    if (draft.mode !== 'tiers' && draft.mode !== 'free') return null;
    const tiersReason = draft.mode === 'tiers' ? tiersInvalid(draft) : null;
    if (tiersReason) return tiersReason;
    return draft.agbConfirmed ? null : 'error_agb';
};

/** Why the tier rows cannot be stored, or null. */
const tiersInvalid = (draft: CancellationDraft): string | null => {
    if (draft.tiers.length === 0) return 'error_no_tiers';
    if (draft.tiers.length > MAX_CANCELLATION_TIERS) return 'error_too_many';
    const tiers = parsedTiers(draft);
    if (!tiers) return 'error_numbers';
    const sorted = [...tiers].sort((a, b) => b.daysBefore - a.daysBefore);
    return sorted.reduce<string | null>((reason, tier, i) => {
        const previous = sorted[i - 1];
        if (reason || !previous) return reason;
        if (tier.daysBefore === previous.daysBefore) return 'error_duplicate';
        return tier.percent < previous.percent ? 'error_falling' : null;
    }, null);
};

/** Does the cancellation section block the form's Save (only where it is shown)? */
export const cancellationBlocksSave = (
    draft: CancellationDraft,
    useCase: string,
    isStandard: boolean,
): boolean => cancellationShown(useCase, isStandard) && cancellationDraftInvalid(draft) !== null;

/** Is the section configurable on this category at all? */
export const cancellationShown = (useCase: string, isStandard: boolean): boolean =>
    (isStandard ? cancellationOnUseCase(useCase) : cancellationOnCategory(useCase));

/**
 * The value to PUT, and whether to send it at all — `send: false` leaves the
 * field out, and the server then keeps what is stored.
 *
 * - Standard category: untouched → not sent (the server keeps the use case's
 *   rule, review of #3394); `none` → null (no rule).
 * - Further category: `inherit` → null when it had its own value (clears it),
 *   else not sent; `none` → `'none'`. Sent while untouched too, because a
 *   further category's otherwise-empty body would clear everything.
 * - Fee tiers that do not read as numbers are never sent — never turned into
 *   `tiers: []`, which would mean "free until the start" (the save gate stops
 *   this case first; this is the second line).
 */
export const cancellationFromDraft = (
    draft: CancellationDraft,
    isStandard: boolean,
): { send: boolean, value: TenantCancellationRule | 'none' | null } => {
    if (isStandard && !draft.touched) return { send: false, value: null };
    if (draft.mode === 'inherit') return { send: draft.stored, value: null };
    if (draft.mode === 'none') return { send: true, value: isStandard ? null : 'none' };
    const tiers = draft.mode === 'free' ? [] : parsedTiers(draft);
    if (!tiers) return { send: false, value: null };
    return {
        send: true,
        value: { tiers: [...tiers].sort((a, b) => b.daysBefore - a.daysBefore), agbConfirmed: true },
    };
};

/** The tiers of a draft, furthest first, for the one-line preview — empty when unreadable. */
export const previewTiers = (draft: CancellationDraft): Array<{ daysBefore: number, percent: number }> =>
    [...(parsedTiers(draft) ?? [])].sort((a, b) => b.daysBefore - a.daysBefore);
